import React from "react";
import classNames from "classnames";

const DGXSparkModel = ({
  width,
  height,
  className,
}: {
  width?: number | string;
  height?: number | string;
  className?: string;
}) => {
  // === COLOR PALETTE ===
  const colors = {
    chassisTop: "#A68B5B",
    chassisMid: "#8C734B",
    chassisBot: "#5E4D33",
    metalDark: "#2b2215",
    metalBase: "#695639",
    metalHighlight: "#c7b28f",
    handleHi: "#E6D5B3",
    handleMid: "#C4AF85",
    handleLo: "#8F7A54",
  };

  const chassisStyle: React.CSSProperties = {
    background: `linear-gradient(180deg, ${colors.chassisTop} 0%, ${colors.chassisMid} 40%, ${colors.chassisBot} 100%)`,
    boxShadow: "0 30px 60px -15px rgba(0, 0, 0, 0.8)",
    width: width
      ? typeof width === "number"
        ? `${width}px`
        : width
      : undefined,
    height: height
      ? typeof height === "number"
        ? `${height}px`
        : height
      : undefined,
    ...(!width && !height ? { width: "100%" } : {}),
  };

  const metallicRecessStyle = {
    background: `linear-gradient(140deg, ${colors.metalDark} 0%, ${colors.metalBase} 45%, ${colors.metalHighlight} 100%)`,
    boxShadow:
      "inset 8px 8px 30px rgba(0,0,0,0.9), inset -3px -3px 6px rgba(255,250,220,0.6)",
    border: `1px solid rgba(0,0,0,0.3)`,
  };

  const handleStyle = {
    background: `linear-gradient(to right, ${colors.handleHi} 0%, ${colors.handleMid} 50%, ${colors.handleLo} 100%)`,
    boxShadow: [
      "2px 2px 12px rgba(0,0,0,0.6)",
      "0px 16px 24px -8px rgba(0,0,0,1.0)",
      "inset 0px 0px 8px 0px rgba(0,0,0,0.25)",
      "inset 0px -15px 20px -5px rgba(0,0,0,0.6)",
    ].join(", "),
  };

  return (
    <div
      className={classNames(
        "bg-transparent flex items-center relative",
        className
      )}
    >
      <div
        className="relative aspect-[3.8/1] rounded-lg overflow-hidden flex-none"
        style={chassisStyle}
      >
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ mixBlendMode: "luminosity", opacity: 1.0 }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <filter id="refinedGlitter-static">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.35"
              numOctaves="5"
              stitchTiles="stitch"
            />
            <feColorMatrix
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 45 -20"
            />
          </filter>
          <rect
            width="100%"
            height="100%"
            filter="url(#refinedGlitter-static)"
          />
        </svg>

        <div className="relative z-10 w-full h-full flex justify-between px-[1%] py-[1%]">
          <div className="w-[18%] h-full flex items-center justify-center pl-[0.5%]">
            <div
              className="w-[90%] h-[78%] rounded-xl flex items-center justify-between px-[6%] relative overflow-hidden"
              style={metallicRecessStyle}
            >
              <div
                className="w-[26%] h-[85%] rounded-full relative z-10"
                style={handleStyle}
              />
              <div className="w-[40%] h-full flex items-center justify-center relative z-10 opacity-95">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  id="Logo"
                  viewBox="0 0 3026.96856 1065.97998"
                  className="w-full h-auto transform rotate-90 scale-[3]"
                >
                  <g id="NVIDIA_Logo">
                    <path
                      id="Eye_Mark"
                      className="fill-[#76b900]"
                      d="M562.33002,440.32999v-41.51999c4-.28,8.09998-.5,12.25-.63,113.54999-3.57001,188,97.56,188,97.56,0,0-80.42004,111.75-166.67999,111.75-11.40808.05426-22.74811-1.75977-33.57001-5.37v-125.88c44.21002,5.34,53.09003,24.85999,79.67999,69.16003l59.09998-49.84s-43.10999-56.56003-115.87-56.56003c-7.65527.0416-15.3028.49222-22.90997,1.35001M562.33002,303.21002v62c4.08002-.32001,8.15997-.57999,12.25-.73001,157.90997-5.33002,260.76996,129.51999,260.76996,129.51999,0,0-118.15997,143.65002-241.25,143.65002-10.65167.00146-21.28278-.93549-31.77002-2.79999v38.33002c8.77472,1.133,17.61249,1.70746,26.46002,1.71997,114.54999,0,197.39001-58.5,277.62-127.73999,13.28998,10.65002,67.73999,36.54999,78.94,47.90997-76.28003,63.84998-254,115.31-354.79999,115.31-9.71002,0-19-.58002-28.21997-1.46002v53.87h435.40997v-459.59998l-435.40997.02002ZM562.33002,602.14001v32.72998c-105.95001-18.87-135.33002-129.04001-135.33002-129.04001,0,0,50.87-56.37,135.35999-65.5v35.91h-.15997c-44.34003-5.32999-79,36.09998-79,36.09998,0,0,19.41,69.72998,79.14001,89.79999M374.14999,501.04999s62.85001-92.65997,188.18002-102.23999v-33.60999c-138.87003,11.14999-259.14001,128.79999-259.14001,128.79999,0,0,68.10999,196.89001,259.14001,214.91998v-35.73999c-140.18002-17.63-188.18002-172.13-188.18002-172.13Z"
                    />
                    <path
                      id="NVIDIA"
                      className="fill-[#C4AF85]"
                      d="M1782,390v301.67999h85.19995v-301.67999h-85.19995ZM1111.78003,389.59v302.12003h86v-229.38004h66.58997c22.06006,0,37.77002,5.48001,48.40002,16.82999,13.47998,14.35999,19,37.51001,19,79.87v132.68005h83.27002v-166.90002c0-119.12-75.93005-135.19-150.20996-135.19h-153M1919.2301,390.04999v301.66003h138.19006c73.62988,0,97.65991-12.25,123.6499-39.70001,18.37012-19.27002,30.23999-61.58002,30.23999-107.82001-0-42.39999-10.05005-80.23001-27.58008-103.78-31.56006-42.13-77-50.36002-144.92004-50.36002h-119.57983ZM2003.75012,455.72998h36.63c53.13989,0,87.51001,23.87,87.51001,85.78998s-34.37012,85.79999-87.51001,85.79999h-36.63v-171.58997ZM1659.21008,390.04999l-71.09998,239.08002-68.14001-239.07001h-92l97.30994,301.65997h122.79004l98.06995-301.66-86.92993-.00998ZM2250.9502,691.70996h85.20996v-301.64996h-85.22998l.02002,301.64996ZM2489.79028,390.14996l-119,301.46002h84l18.82007-53.28998h140.80005l17.82007,53.28998h91.20996l-119.88037-301.47998-113.76978.01996ZM2545.10034,445.14996l51.61011,141.22998h-104.89038l53.28027-141.22998Z"
                    />
                  </g>
                </svg>
              </div>
            </div>
          </div>

          <div className="w-[18%] h-full flex items-center justify-center pr-[0.5%]">
            <div
              className="w-[90%] h-[78%] rounded-xl flex items-center justify-between px-[6%] relative"
              style={metallicRecessStyle}
            >
              <div className="w-[40%]"></div>
              <div
                className="w-[26%] h-[85%] rounded-full relative z-10"
                style={handleStyle}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DGXSparkModel;
